import {useMemo} from 'react'
import {useQuery} from '@apollo/client'
import {Alert, Badge, Button, Card, Spinner, Table} from 'react-bootstrap'
import {FormattedMessage} from 'react-intl'
import {useLocation, useNavigate} from 'react-router-dom'
import {GET_TICKETS} from '../../../gql/queries/ticketsQuery'
import {parseId} from '../../../helpers'
import {IResTickets, ITicket} from '../../../types/tickets'

type PolicyTicketsTabProps = {
  policyId: string
}

const dash = '-'
const hash = '#'

const formatDate = (value?: string | null) => {
  if (!value) return dash
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? dash : date.toLocaleString()
}

const userLabel = (user?: {firstName?: string; lastName?: string; email?: string} | null) => {
  if (!user) return dash
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return full || user.email || dash
}

const assignmentLabel = (ticket?: Pick<ITicket, 'assignedGroup' | 'assignedUser'> | null) => {
  if (!ticket) return dash
  const groupName = ticket.assignedGroup?.name || ''
  const assignee = userLabel(ticket.assignedUser)
  if (groupName && assignee !== dash) return `${groupName} / ${assignee}`
  if (groupName) return groupName
  return assignee
}

const statusVariant = (status?: string | null) => {
  if (status === 'OPEN') return 'warning'
  if (status === 'IN_PROGRESS') return 'primary'
  if (status === 'WAITING_CUSTOMER') return 'info'
  if (status === 'RESOLVED') return 'success'
  return 'secondary'
}

const priorityVariant = (priority?: string | null) => {
  if (priority === 'URGENT') return 'danger'
  if (priority === 'HIGH') return 'warning'
  if (priority === 'MEDIUM') return 'info'
  return 'secondary'
}

export const PolicyTicketsTab = ({policyId}: PolicyTicketsTabProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const normalizedPolicyId = useMemo(() => parseId(policyId), [policyId])
  const {data, loading, error} = useQuery<IResTickets>(GET_TICKETS, {
    variables: {
      policyId: normalizedPolicyId,
      first: 100,
      offset: 0,
      includeArchived: true,
    },
    skip: !normalizedPolicyId,
    fetchPolicy: 'no-cache',
  })

  const tickets = data?.tickets?.items || []

  if (loading) {
    return (
      <Card className='cardContainer'>
        <Card.Body className='d-flex align-items-center gap-3'>
          <Spinner animation='border' size='sm' />
          <span>
            <FormattedMessage
              id='policies.detail.tab.tickets.loading'
              defaultMessage='Loading policy tickets...'
            />
          </span>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant='danger' className='mb-0'>
        <FormattedMessage
          id='policies.detail.tab.tickets.errorLoading'
          defaultMessage='Error loading policy tickets: {message}'
          values={{message: error.message}}
        />
      </Alert>
    )
  }

  return (
    <Card className='cardContainer'>
      <Card.Body>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4'>
          <div>
            <h4 className='mb-1'>
              <FormattedMessage id='policies.detail.tab.tickets.title' defaultMessage='Policy Tickets' />
            </h4>
            <p className='text-muted mb-0'>
              <FormattedMessage
                id='policies.detail.tab.tickets.subtitle'
                defaultMessage='Tickets linked directly to this policy.'
              />
            </p>
          </div>
          <Button
            size='sm'
            variant='primary'
            onClick={() => {
              const params = new URLSearchParams(location.search)
              params.set('tab', 'Tickets')
              const returnTo = encodeURIComponent(`${location.pathname}?${params.toString()}`)
              navigate(`/tickets/new?policyId=${normalizedPolicyId}&returnTo=${returnTo}`)
            }}
          >
            <i className='bi bi-ticket-perforated me-2' /> <FormattedMessage
              id='policies.detail.tab.tickets.add'
              defaultMessage='Add Ticket'
            />
          </Button>
        </div>

        {tickets.length === 0 ? (
          <Alert variant='light' className='mb-0'>
            <FormattedMessage
              id='policies.detail.tab.tickets.empty'
              defaultMessage='No tickets are associated with this policy yet.'
            />
          </Alert>
        ) : (
          <div className='table-responsive'>
            <Table hover className='align-middle mb-0'>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.id' defaultMessage='ID' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.subject' defaultMessage='Subject' />
                  </th>
                  <th>
                    <FormattedMessage id='common.field.status' defaultMessage='Status' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.priority' defaultMessage='Priority' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.assignedTo' defaultMessage='Assigned To' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.type' defaultMessage='Type' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.tab.tickets.th.updated' defaultMessage='Updated' />
                  </th>
                  <th className='text-end'>
                    <FormattedMessage id='policies.detail.tab.tickets.th.action' defaultMessage='Action' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>{hash}{parseId(ticket.id)}</td>
                    <td>{ticket.title}</td>
                    <td>
                      <Badge bg={statusVariant(ticket.status)}>
                        {ticket.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={priorityVariant(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </td>
                    <td>{assignmentLabel(ticket)}</td>
                    <td>{ticket.ticketType?.name || dash}</td>
                    <td>{formatDate(ticket.updatedAt)}</td>
                    <td className='text-end'>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        onClick={() => {
                          const params = new URLSearchParams(location.search)
                          params.set('tab', 'Tickets')
                          const returnTo = encodeURIComponent(`${location.pathname}?${params.toString()}`)
                          navigate(`/tickets/${parseId(ticket.id)}?returnTo=${returnTo}`)
                        }}
                      >
                        <FormattedMessage id='policies.detail.tab.tickets.open' defaultMessage='Open' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
